import { useEffect, useState } from "react";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { IPermission } from "../../../../common/data/dtos/permission";
import { IPermissionScreen } from "../../../../common/data/dtos/permission-screen";
import { IScreen } from "../../../../common/data/dtos/screen";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardBody, CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import InputGroup from "../../../../components/bootstrap/forms/InputGroup";
import Select from "../../../../components/bootstrap/forms/Select";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import permissionScreenService from "../../../../services/permission-screen-service";
import screenService from "../../../../services/screen-service";

interface Props {
    isModalOpened: boolean;
    selectedPermission: IPermission | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const PermissionScreenModal = ({ isModalOpened, selectedPermission, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [screens, setScreens] = useState<IScreen[]>([]);
    const [permissionsScreen, setPermissionsScreen] = useState<IPermissionScreen[]>([]);
    const [selectedScreenId, setSelectedScreenId] = useState<string>('');

    useEffect(() => {

        if (selectedPermission) {
            fetchData();
            setSelectedScreenId('');
        }

    }, [isModalOpened]);

    const fetchData = async () => {

        setIsSubmitting(true);

        try {
            if (!selectedPermission?.id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION)
            } else {

                const [screenResponse, permissionScreenResponse] = await Promise.all([
                    screenService.getScreens(),
                    permissionScreenService.getPermissionScreensByPermission(selectedPermission.id),
                ]);

                if (screenResponse.error) {
                    showWarning(screenResponse.error);
                    return;
                }

                if (permissionScreenResponse.error) {
                    showWarning(permissionScreenResponse.error);
                    return;
                }

                setScreens(screenResponse.screens || []);
                setPermissionsScreen(permissionScreenResponse.permissionScreens || []);
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }

    }

    const fetchPermissionsScreen = async () => {

        if (selectedPermission?.id) {

            const response = await permissionScreenService.getPermissionScreensByPermission(selectedPermission?.id)

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setPermissionsScreen(response.permissionScreens || []);
        }
    }

    const onFormSubmit = async () => {

        if (!permissionsScreen) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION)
            return;
        }

        const itensToAdd = permissionsScreen
            .filter(item => !item.id || item.id.trim() === '')
            .map(({ screen, ...toAdd }) => toAdd);


        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD)
            return;
        }

        setIsSubmitting(true);

        const response = await permissionScreenService.createPermissionScreens(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        setSelectedScreenId('');
        onAfterSubmit();
    }

    const onClose = () => {
        onModalClose();
    };

    const handleAddClick = async () => {

        if (!selectedPermission?.id || !selectedScreenId) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const exists = permissionsScreen.some(item => item.screenId === selectedScreenId);
        if (exists) {
            showWarning(MESSAGES.VALIDATION.ITEM_EXISTS);
            return;
        } else {

            const selectedScreen = screens.find(p => p.id === selectedScreenId);

            const newPermissionScreen: IPermissionScreen = {
                permissionId: selectedPermission?.id,
                screenId: selectedScreenId,
                rowVersion: 1,
                status: StatusEnum.Active,
                screen: selectedScreen
            };

            setPermissionsScreen((prev) => [
                ...prev,
                newPermissionScreen,
            ]);
        }
    }

    const handleDeleteClick = async (permissionScreen: IPermissionScreen) => {
        if (!permissionScreen.id) {
            const filteredList = permissionsScreen.filter(item => item.screenId !== permissionScreen.screenId);
            setPermissionsScreen(filteredList);
        } else {

            setIsSubmitting(true);

            const response = await permissionScreenService.deletePermissionScreen(permissionScreen.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchPermissionsScreen();
        }
    }

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='permission-screen-modal'>Manutenção de permissões da tela</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title={selectedPermission?.name || 'Manutenção de permissões da tela'} icon='Lock'>
                        <CardBody isScrollable style={{ minHeight: 400 }}>
                            <div className='row g-4'>
                                <div className='col-lg-12 col-md-12 col-sm-12'>
                                    <InputGroup
                                        isWrap={true}
                                    >
                                        <FormGroup id="screenId" label="Tela" isFloating>
                                            <Select
                                                ariaLabel='Escolha uma tela'
                                                list={[
                                                    { value: '', text: 'Selecione uma tela' },
                                                    ...screens.map((screen) => ({
                                                        value: screen.id,
                                                        text: `${screen.name}`
                                                    }))
                                                ]}
                                                onChange={(e: { target: { value: any } }) => {
                                                    if (e.target.value) {
                                                        setSelectedScreenId(e.target.value);
                                                    }
                                                }}
                                            />
                                        </FormGroup>

                                        <Button
                                            icon='AddCircleOutline'
                                            color='primary'
                                            onClick={handleAddClick}
                                        >
                                            Adicionar
                                        </Button>
                                    </InputGroup>
                                </div>

                                <div className='col-lg-12 col-md-12 col-sm-12 table-responsive '>

                                    <table className='table table-modern table-hover'>
                                        <thead>
                                            <tr>
                                                <th>Nome</th>
                                                <th>Chave</th>
                                                <td aria-labelledby='Actions' />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {permissionsScreen.map((item: IPermissionScreen) => (
                                                <tr key={item.id}>
                                                    <td>{item.screen?.name}</td>
                                                    <td>{item.screen?.key}</td>

                                                    <td className='text-end'>
                                                        <Button
                                                            color='dark'
                                                            isLight
                                                            icon='Delete'
                                                            onClick={() => handleDeleteClick(item)}
                                                            aria-label='Remover'
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </CardBody>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
}